import { CopyableId, ErrorState, LoadingState, Stat, StatusPill } from '../../components';
import { formatCents, formatCount } from '../../lib/format/money';
import { formatDate, formatDateTime } from '../../lib/format/dates';
import { localeLabel } from '../locales/api';
import { useLocalesById } from '../locales/queries';
import { useListingDetail } from '../orders/queries';
import { Field, FieldGrid, KeyValues, Section, formatAddress, isEmptyObject, tagList } from '../orders/detailParts';
import { dishStatus, humanize } from '../profiles/people';
import type { ListingDetail } from './api';

const SHARE_LEVEL: Record<number, string> = { 1: 'Direct connections', 2: 'Connections of connections', 3: 'Anyone' };

/**
 * Dish Detail — every field of a listing and its dish
 * (`GET /admin/listings/{id}/detail`). Used on its own in the Dish dialog and
 * below the order in the Order dialog.
 */
export function DishDetail({ listingId }: { listingId: string }) {
  const detail = useListingDetail(listingId);
  if (detail.isPending) return <LoadingState label="Loading dish…" />;
  if (detail.isError) return <ErrorState error={detail.error} onRetry={() => void detail.refetch()} compact />;
  return <DishDetailBody detail={detail.data} />;
}

function DishDetailBody({ detail }: { detail: ListingDetail }) {
  const { listing, dish, chef, stats } = detail;
  const [locale] = useLocalesById(listing.localeId ? [listing.localeId] : []);
  const status = dish ? dishStatus(dish.status) : null;

  return (
    <div>
      <div className="mb-[18px] flex items-start gap-4 pr-8">
        {listing.thumbnailUrl || listing.imageUrl ? (
          <img
            src={listing.thumbnailUrl || listing.imageUrl}
            alt=""
            className="h-[72px] w-[72px] shrink-0 rounded-app object-contain"
          />
        ) : null}
        <div className="min-w-0 flex-1">
          <div className="truncate font-heading text-2xl font-bold tracking-[-.02em] text-brown-deep">
            {listing.title || 'Untitled'}
          </div>
          <div className="mt-[3px] text-[13px] text-muted">
            {chef?.displayName ? `By ${chef.displayName}` : 'Chef unknown'}
          </div>
          <div className="mt-0.5">
            <CopyableId id={listing.id} label="Listing ID" />
          </div>
        </div>
        {status && (
          <StatusPill tone={status.tone} className="shrink-0">
            {status.label}
          </StatusPill>
        )}
      </div>

      <div className="grid grid-cols-4 gap-2.5">
        <Stat label="Price" value={formatCents(listing.price)} />
        <Stat label="Quantity" value={formatCount(listing.quantity)} hint="portions offered" />
        <Stat label="Orders" value={formatCount(stats.ordersPlaced)} hint="completed" />
        <Stat label="Sold" value={formatCount(stats.portionsSold)} hint="portions" />
      </div>

      <Section title="Listing">
        <FieldGrid>
          <Field label="Published">{listing.publishedAt ? formatDateTime(listing.publishedAt) : 'Not published'}</Field>
          <Field label="Created">{formatDateTime(listing.createdAt)}</Field>
          <Field label="Locale">
            {listing.localeId && (locale?.data ? localeLabel(locale.data) : listing.localeId)}
          </Field>
          <Field label="Type">{humanize(listing.type)}</Field>
          <Field label="Fulfillment">{dish?.fulfillmentMethod?.map(humanize).join(', ')}</Field>
          <Field label="Visible to">{SHARE_LEVEL[listing.shareConnectionLevel] ?? listing.shareConnectionLevel}</Field>
          <Field label="Location" wide>
            {formatAddress(listing.listingLocation) || listing.obfuscatedLocation}
          </Field>
          <Field label="Auto-ready">{listing.autoReadyEnabled ? 'On' : 'Off'}</Field>
          <Field label="Language">{listing.authoredLang}</Field>
          {listing.cancellationReason && (
            <Field label="Cancellation reason" wide>
              {listing.cancellationReason}
            </Field>
          )}
          <Field label="Description" wide>
            {listing.description && <span className="whitespace-pre-wrap">{listing.description}</span>}
          </Field>
        </FieldGrid>
      </Section>

      <Section title="Dish">
        <FieldGrid>
          <Field label="Dietary">{tagList(dish?.dietaryTags)}</Field>
          <Field label="Allergens">{tagList(dish?.allergenTags)}</Field>
          <Field label="Origin">{tagList(dish?.originTags)}</Field>
          <Field label="Pickup window">
            {!isEmptyObject(listing.timeWindow) && <KeyValues value={listing.timeWindow} />}
          </Field>
          <Field label="Ingredients" wide>
            {!isEmptyObject(dish?.ingredients) && <KeyValues value={dish?.ingredients} />}
          </Field>
        </FieldGrid>
      </Section>

      <div className="mt-1 text-xs text-muted">Last updated {formatDate(listing.updatedAt)}</div>
    </div>
  );
}
